// app-shell — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    background: var(--run-app-shell-background, var(--run-color-surface-default));
    color: var(--run-app-shell-color, var(--run-color-text-primary));
  }

  .shell-frame {
    display: flex;
    flex-direction: column;
    /* Fill the host rather than imposing our own viewport height. :host
       keeps min-height:100dvh as the default full-page sticky-footer
       behaviour; a consumer that sizes the host (embedded shell) gets a
       frame that fits — without this, .shell-frame's own 100dvh pushed
       the footer past any non-viewport-height container. */
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
  }

  .shell-header {
    position: sticky;
    top: 0;
    z-index: var(--run-shell-z-index-header, 100);
    height: var(--run-shell-header-height, 3.5rem);
    background: var(--run-app-shell-header-background, var(--run-color-surface-raised));
    border-bottom: 1px solid var(--run-app-shell-header-border-color, var(--run-color-border-default));
    box-shadow: var(--run-app-shell-header-shadow, var(--run-elevation-semantic-raised));
    display: flex;
    align-items: center;
  }

  .shell-body {
    display: flex;
    flex: 1 1 auto;
    overflow: hidden;
    max-width: var(--run-shell-content-max-width, 87.5rem);
    width: 100%;
    margin-inline: auto;
  }

  .shell-side-nav {
    flex: 0 0 var(--run-shell-side-nav-width, 16rem);
    width: var(--run-shell-side-nav-width, 16rem);
    background: var(--run-app-shell-sidebar-background, var(--run-color-surface-raised));
    border-right: 1px solid var(--run-app-shell-sidebar-border-color, var(--run-color-border-default));
    overflow-y: auto;
    transition: transform 0.25s ease;
  }

  .shell-side-nav[aria-hidden="true"] {
    display: none;
  }

  .shell-main {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: var(--run-app-shell-padding-block, var(--run-space-semantic-sm)) var(--run-app-shell-padding-inline, var(--run-space-semantic-sm));
  }

  .shell-footer {
    background: var(--run-app-shell-footer-background, var(--run-color-surface-raised));
    border-top: 1px solid var(--run-app-shell-footer-border-color, var(--run-color-border-default));
  }

  .shell-route-announcer {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .shell-scrim {
    display: none;
    position: fixed;
    inset: 0;
    z-index: var(--run-shell-z-index-scrim, 190);
    background: var(--run-shell-scrim-color, rgba(0,0,0,0.45));
  }

  /* Drawer mode is selected by the headless via [data-layout="drawer"]
     (it reads the configurable --run-shell-breakpoint against the host's
     own width — CSS @media/@container cannot read custom properties, and
     the breakpoint decision is behaviour, not presentation). The skin is
     query-free: it only expresses the visual states. */
  :host([data-layout="drawer"]) .shell-side-nav {
    position: fixed;
    inset-block: 0;
    inset-inline-start: 0;
    z-index: var(--run-shell-z-index-drawer, 200);
    transform: translateX(-100%);
  }

  :host([data-layout="drawer"][nav-open]) .shell-side-nav {
    transform: translateX(0);
  }

  :host([data-layout="drawer"][nav-open]) .shell-scrim {
    display: block;
  }
`;

export function render(host) {
  const { navOpen, _hasSideNav } = host; // declared in MVA SkinModule.render contract
  host.shadowRoot.innerHTML = `
    <div class="shell-frame">
      <header class="shell-header">
        <slot name="header"></slot>
      </header>
      <div class="shell-body">
        <nav class="shell-side-nav" aria-hidden="${_hasSideNav ? 'false' : 'true'}">
          <slot name="side-nav"></slot>
        </nav>
        <main class="shell-main">
          <slot name="main"></slot>
        </main>
      </div>
      <footer class="shell-footer">
        <slot name="footer"></slot>
      </footer>
      <div class="shell-route-announcer">
        <slot name="route-announcer"></slot>
      </div>
      <div class="shell-scrim" aria-hidden="true"></div>
    </div>
  `;
}